import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import { SectionCard } from "../Section";

const TINT_BAR: Record<"teal" | "violet" | "amber", string> = {
  teal: "bg-teal-500",
  violet: "bg-primary",
  amber: "bg-amber-500",
};

const TINT_ICON: Record<"teal" | "violet" | "amber", string> = {
  teal: "bg-teal-500/12 text-teal-600 dark:text-teal-400",
  violet: "bg-primary/12 text-primary",
  amber: "bg-amber-500/12 text-amber-600 dark:text-amber-400",
};

export function GlanceStatCard({
  label,
  value,
  tint,
  icon,
  loading,
  className,
}: {
  label: string;
  value: number;
  tint: "teal" | "violet" | "amber";
  icon: ReactNode;
  loading?: boolean;
  className?: string;
}) {
  return (
    // The number and its label are separate paragraphs, so assistive tech read
    // them as two unrelated fragments (or dropped the number entirely). One
    // labelled group keeps "16 due now" together.
    <SectionCard
      className={cn("relative flex min-h-[7.5rem] flex-col overflow-hidden p-0", className)}
      role="group"
      aria-label={loading ? label : `${value} ${label}`}
    >
      <div className="flex flex-1 flex-col gap-3 p-4 pb-5" aria-hidden="true">
        <span className={cn("grid size-10 place-items-center rounded-xl", TINT_ICON[tint])}>
          {icon}
        </span>
        <div className="leading-tight">
          <p className="font-display text-2xl font-semibold tabular-nums">
            {loading ? <Loader2 className="text-muted-foreground size-6 animate-spin" /> : value}
          </p>
          <p className="text-muted-foreground mt-0.5 text-xs font-medium">{label}</p>
        </div>
      </div>
      <div className={cn("h-1 w-full", TINT_BAR[tint])} aria-hidden="true" />
    </SectionCard>
  );
}
